import { TrashIcon } from '@phosphor-icons/react';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import colors from 'tailwindcss/colors';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useApiMutation } from '@/modules/common';

interface InventoryRemoveButtonProps {
  eventId: string;
  productId: string;
  sizeId?: string;
  stationId: string;
  isLastSize?: boolean;
  onSuccess: () => void;
}

export function InventoryRemoveButton({
  eventId,
  productId,
  sizeId,
  stationId,
  isLastSize,
  onSuccess,
}: InventoryRemoveButtonProps) {
  const queryClient = useQueryClient();
  const [open, setIsOpen] = useState(false);

  const removeEventProductSizeMutation = useApiMutation<void>(
    `/events/${eventId}/stations/${stationId}/products/${productId}/sizes/${sizeId}`,
    {
      method: 'DELETE',
    },
    {
      onError: () => {
        toast.error('Uh oh! Something went wrong.');
      },
      onSuccess,
    }
  );

  const removeEventProductMutation = useApiMutation<void>(
    `/events/${eventId}/stations/${stationId}/products/${productId}`,
    {
      method: 'DELETE',
    },
    {
      onError: () => {
        toast.error('Uh oh! Something went wrong.');
      },
      onSuccess: () => {
        onSuccess();
        queryClient.invalidateQueries({
          queryKey: ['eventDetails', String(eventId)],
        });
      },
    }
  );

  const submitRemoveInventory = async () => {
    //  Case 1: no sizeId → remove the entire product
    if (!sizeId) {
      await removeEventProductMutation.mutateAsync();
    } else {
      //  Case 2: remove the size first
      await removeEventProductSizeMutation.mutateAsync();
    }
    setIsOpen(false);
  };

  const handleRemoveInventory = async () => {
    setIsOpen(true);
  };

  const isPending =
    removeEventProductSizeMutation.isPending ||
    removeEventProductMutation.isPending;
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        type="button"
        onClick={handleRemoveInventory}
        data-testid="remove-inventory"
      >
        <TrashIcon size={28} color={colors.zinc[400]} />
      </Button>
      <AlertDialog open={open}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this {sizeId ? 'size' : 'product'}
              ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={submitRemoveInventory}
              className="text-destructive border-destructive hover:text-destructive"
              data-testid="shut-down-button"
            >
              {isPending && <CircleNotchIcon className="animate-spin" />}
              Delete
            </AlertDialogAction>
            <AlertDialogCancel onClick={() => setIsOpen(false)}>
              Cancel
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
